import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import PersonIcon from '@mui/icons-material/Person';
import InventoryIcon from '@mui/icons-material/Inventory';
import dayjs from 'dayjs';
import { getEmployees, getInventoryItems } from '../../services/api';

const InventoryAssignmentForm = ({ 
  open, 
  mode, 
  formData, 
  formErrors, 
  onClose, 
  onSubmit, 
  onInputChange 
}) => {
  const [employees, setEmployees] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedInventoryItem, setSelectedInventoryItem] = useState(null);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingInventoryItems, setLoadingInventoryItems] = useState(false);
  const [stockWarning, setStockWarning] = useState('');

  // Load employees
  useEffect(() => {
    const loadEmployees = async () => {
      if (!open) return;
      
      setLoadingEmployees(true);
      try {
        const response = await getEmployees(1, 100); // Get first 100 employees
        setEmployees(response.data?.results || []);
      } catch (error) {
        console.error('Error loading employees:', error);
      } finally {
        setLoadingEmployees(false);
      }
    };

    loadEmployees();
  }, [open]);

  // Load inventory items
  useEffect(() => {
    const loadInventoryItems = async () => {
      if (!open) return;
      
      setLoadingInventoryItems(true);
      try {
        const response = await getInventoryItems(1, 100); // Get first 100 items
        setInventoryItems(response.data?.results || []);
      } catch (error) {
        console.error('Error loading inventory items:', error);
      } finally {
        setLoadingInventoryItems(false);
      }
    };

    loadInventoryItems();
  }, [open]);

  // Update selected employee when form data changes
  useEffect(() => {
    if (formData.assigned_to_id && employees.length > 0) {
      const employee = employees.find(emp => emp.id.toString() === formData.assigned_to_id.toString());
      setSelectedEmployee(employee);
    } else {
      setSelectedEmployee(null);
    }
  }, [formData.assigned_to_id, employees]);

  // Update selected inventory item when form data changes
  useEffect(() => {
    if (formData.inventory_item_id && inventoryItems.length > 0) {
      const item = inventoryItems.find(item => item.id.toString() === formData.inventory_item_id.toString());
      setSelectedInventoryItem(item);
    } else {
      setSelectedInventoryItem(null);
    }
  }, [formData.inventory_item_id, inventoryItems]);

  // Check stock availability
  useEffect(() => {
    if (selectedInventoryItem && formData.quantity) {
      const requestedQuantity = parseInt(formData.quantity, 10) || 0;
      if (selectedInventoryItem.quantity <= 0) {
        setStockWarning(`Warning: ${selectedInventoryItem.name} is out of stock!`);
      } else if (requestedQuantity > selectedInventoryItem.quantity) {
        setStockWarning(`Warning: Requested quantity (${requestedQuantity}) exceeds available stock (${selectedInventoryItem.quantity})`);
      } else if (selectedInventoryItem.quantity <= 5) {
        setStockWarning(`Warning: ${selectedInventoryItem.name} has low stock (${selectedInventoryItem.quantity} remaining)`);
      } else {
        setStockWarning('');
      }
    } else {
      setStockWarning('');
    }
  }, [selectedInventoryItem, formData.quantity]);

  const handleSubmit = () => {
    // Prevent submission if item is out of stock or requested quantity exceeds available
    const requestedQuantity = parseInt(formData.quantity, 10) || 0;
    if (selectedInventoryItem && (selectedInventoryItem.quantity <= 0 || requestedQuantity > selectedInventoryItem.quantity)) {
      return; // Don't submit
    }
    onSubmit();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === 'add' ? 'New Assignment' : mode === 'edit' ? 'Edit Assignment' : 'Assignment Details'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControl fullWidth error={!!formErrors.assigned_to_id}>
              <InputLabel required>Employee</InputLabel>
              <Select
                value={formData.assigned_to_id || ''}
                label="Employee"
                onChange={(e) => onInputChange('assigned_to_id', e.target.value)}
                disabled={mode === 'view' || loadingEmployees}
              >
                {employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    {employee.full_name || `${employee.first_name} ${employee.last_name}`} 
                    {employee.position && ` - ${employee.position}`}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.assigned_to_id && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                  {formErrors.assigned_to_id}
                </Typography>
              )}
            </FormControl>
            {selectedEmployee && (
              <Box sx={{ mt: 1, p: 1, bgcolor: 'info.lighter', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon fontSize="small" />
                  Employee: {selectedEmployee.full_name || `${selectedEmployee.first_name} ${selectedEmployee.last_name}`}
                  {selectedEmployee.email && ` (${selectedEmployee.email})`}
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth error={!!formErrors.inventory_item_id}>
              <InputLabel required>Inventory Item</InputLabel>
              <Select
                value={formData.inventory_item_id || ''}
                label="Inventory Item"
                onChange={(e) => onInputChange('inventory_item_id', e.target.value)}
                disabled={mode === 'view' || loadingInventoryItems}
              >
                {inventoryItems.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name} (Qty: {item.quantity})
                  </MenuItem>
                ))}
              </Select>
              {formErrors.inventory_item_id && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                  {formErrors.inventory_item_id}
                </Typography>
              )}
            </FormControl>
            {selectedInventoryItem && (
              <Box sx={{ mt: 1, p: 1, bgcolor: 'info.lighter', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InventoryIcon fontSize="small" />
                  Item: {selectedInventoryItem.name} - Available: {selectedInventoryItem.quantity}
                </Typography>
                {selectedInventoryItem.description && (
                  <Typography variant="caption" color="text.secondary">
                    {selectedInventoryItem.description}
                  </Typography>
                )}
              </Box>
            )}
            {stockWarning && (
              <Alert severity={selectedInventoryItem?.quantity <= 0 ? "error" : "warning"} sx={{ mt: 1 }}>
                {stockWarning}
              </Alert>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Quantity"
              type="number"
              value={formData.quantity}
              onChange={(e) => onInputChange('quantity', e.target.value)}
              disabled={mode === 'view'}
              error={!!formErrors.quantity}
              helperText={formErrors.quantity}
              required
              inputProps={{ min: 1, max: selectedInventoryItem?.quantity || 999 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Assignment Date"
                value={formData.assigned_date ? dayjs(formData.assigned_date) : null}
                onChange={(newValue) => {
                  const dateString = newValue ? newValue.format('YYYY-MM-DD') : '';
                  onInputChange('assigned_date', dateString);
                }}
                disabled={mode === 'view'}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: !!formErrors.assigned_date,
                    helperText: formErrors.assigned_date,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Remarks"
              value={formData.remarks}
              onChange={(e) => onInputChange('remarks', e.target.value)}
              disabled={mode === 'view'}
              multiline
              rows={3}
              error={!!formErrors.remarks}
              helperText={formErrors.remarks}
              placeholder="Optional remarks about this assignment..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {mode !== 'view' && (
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            disabled={
              selectedInventoryItem?.quantity <= 0 || 
              (parseInt(formData.quantity, 10) || 0) > (selectedInventoryItem?.quantity || 0)
            }
          >
            {mode === 'add' ? 'Create Assignment' : 'Update Assignment'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

InventoryAssignmentForm.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['add', 'edit', 'view']).isRequired,
  formData: PropTypes.object.isRequired,
  formErrors: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
};

export default InventoryAssignmentForm;